import { SpannerInterface } from './spanner';

export interface AnalyticerInterface {
  /**
   * Starts a analytics sessions.
   * Creates
   */
  start(): void;

  /**
   * Creates a login event.
   */
  login(userId: string): void;

  /**
   * Create a logout event
   */
  logout(): void;

  /**
   * Visit a page. Creates a new span for recording this page visit. Terminates any existing page span.
   * Any events created during the span will be associated with the page span.
   * 
   */
  visit(label: string, attributes: {[key: string]: string}): void;

  /**
   * Create a generic event
   */
  event(label: string, attributes: {[key: string]: string}): void;

  // Stop all analytics and flush any pending events
  end(): void;    
}

export interface AnalyticerStorage {
  getItem(key: string): string | null | undefined,
  setItem(set: string, value: string): void,
}

export class Analyticer implements AnalyticerInterface {

  private tabSpanId: string  = '';
  private pageSpanId: string = '';

  constructor(
    private spanner: SpannerInterface,
    private storage: AnalyticerStorage,
    private getAttributes: () => { [key: string]: string; },
  ) {
    this.spanner.set(this.getDefaultAttributes());
  }

  start(): void {
    this.tabSpanId = this.spanner.open('tab_session', this.getDefaultAttributes());
  }

  setUser(userId: string): void {
    this.spanner.set({user_id: userId});
  }

  login(userId: string): void {
    this.spanner.event(this.tabSpanId, 'login', {user_id: userId});
  }

  logout(): void {
    this.spanner.event(this.tabSpanId, 'logout', {});
  }

  setLastVisitedArticle(articleId: string) {
    this.spanner.set({lva: articleId});
    this.storage.setItem('a.lva', articleId);
  }

  event(label: string, attributes: { [key: string]: string; }): void {
    Object.assign(attributes, this.getDefaultAttributes());
    this.spanner.event(this.pageSpanId || this.tabSpanId, label, attributes);
  }

  visit(label: string, attributes: { [key: string]: string; }): void {
    if(this.pageSpanId) {
      this.spanner.close(this.pageSpanId);
    }

    Object.assign(attributes, this.getDefaultAttributes());
    
    this.pageSpanId = this.spanner.open(label, attributes, this.tabSpanId);
  }

  open(label: string, attributes: { [key: string]: string; } ) {
    Object.assign(attributes, this.getDefaultAttributes());
    return this.spanner.open(label, attributes, this.tabSpanId);
  }

  close(id: string) {
    return this.spanner.close(id);
  }

  end(): void {
    if(this.pageSpanId) {
      this.spanner.close(this.pageSpanId);
      this.pageSpanId = '';
    }

    if(this.tabSpanId) {
      this.spanner.close(this.tabSpanId);
      this.tabSpanId = '';
    }
  }

  private getDefaultAttributes() {
    const attributes: any =  this.getAttributes();

    const offset = new Date().getTimezoneOffset();

    attributes['tz_offset'] = offset;
    attributes['local_time'] = new Date().toLocaleString('en', { timeZoneName: 'short' });

    const lva = this.storage.getItem('a.lva');

    if(lva) attributes['lva'] = lva;
    
    return attributes;
  }
}

